import type { Project } from "../Project";
import { projectConstructionLines } from "../Project";

/** Pure model walkers for mirror back-refs. Lives in its own module
 *  (instead of inline in `mirrorConnection.ts`) so unit tests can
 *  import these without pulling in paperjs-offset / paper-full
 *  through the `constructionMirror.ts → SelectTool` import chain.
 *  Same separation `dragClosure.ts` keeps from `constructionOffset.ts`. */

/** Forward closure for mirror: every line whose `mirrorSource`
 *  back-ref chains back to any origin via either `sourceId` OR
 *  `axis.constructionId`. Undirected for the drag-closure use
 *  case — a snap-pinned-to-mirror should drag the source AND the
 *  axis. */
export function reachableMirrors(
  project: Project,
  originIds: ReadonlyArray<string>,
): Set<string> {
  const out = new Set<string>();
  if (originIds.length === 0) return out;
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return out;
  const dependentsByLine = new Map<string, string[]>();
  for (const l of lines) {
    const link = l.mirrorSource;
    if (!link) continue;
    for (const refId of [link.sourceId, link.axis.constructionId]) {
      if (!dependentsByLine.has(refId)) dependentsByLine.set(refId, []);
      dependentsByLine.get(refId)!.push(l.id);
    }
  }
  const queue: string[] = [...originIds];
  while (queue.length > 0) {
    const cur = queue.shift()!;
    const kids = dependentsByLine.get(cur);
    if (!kids) continue;
    for (const k of kids) {
      if (out.has(k)) continue;
      out.add(k);
      queue.push(k);
    }
  }
  return out;
}

/** Walk up the mirror chain to the non-mirror root. Returns the
 *  input id when the line isn't a mirror derived. Cycle-safe via
 *  step cap. */
export function resolveMirrorRoot(
  project: Project,
  constructionId: string,
): string {
  const lines = projectConstructionLines(project);
  const byId = new Map(lines.map((l) => [l.id, l] as const));
  const seen = new Set<string>();
  let cur = constructionId;
  for (let i = 0; i < 1000; i++) {
    if (seen.has(cur)) return cur;
    seen.add(cur);
    const line = byId.get(cur);
    if (!line?.mirrorSource) return cur;
    cur = line.mirrorSource.sourceId;
  }
  return cur;
}

/** Detect mirror lines whose source chain re-enters themselves
 *  (either directly via `sourceId` or transitively). Lines in the
 *  returned set must be detached (their `mirrorSource` dropped)
 *  before the deriver runs so the deriver can't loop. */
export function detectMirrorCycles(project: Project): Set<string> {
  const cycled = new Set<string>();
  const lines = projectConstructionLines(project);
  if (lines.length === 0) return cycled;
  const byId = new Map(lines.map((l) => [l.id, l] as const));
  for (const line of lines) {
    if (!line.mirrorSource) continue;
    const seen = new Set<string>();
    let cur: string | undefined = line.mirrorSource.sourceId;
    for (let i = 0; i < 1000 && cur; i++) {
      if (cur === line.id) {
        cycled.add(line.id);
        break;
      }
      if (seen.has(cur)) break;
      seen.add(cur);
      const nxt = byId.get(cur);
      if (!nxt?.mirrorSource) break;
      cur = nxt.mirrorSource.sourceId;
    }
  }
  return cycled;
}
